/*
  Lightweight Cookie Consent Manager
  - Handles reading/writing consent cookie
  - Gates third-party scripts via type="text/plain" and data-category attr
  - Emits window event 'consent:updated'
*/

import consentConfig from '../config/consentConfig';

const COOKIE_NAME = consentConfig.cookieName;

// Basic cookie helpers (no external deps)
function setCookie(name, value, days, attrs = {}) {
  const expires = days
    ? `; Expires=${new Date(Date.now() + days * 864e5).toUTCString()}`
    : '';
  const path = attrs.path ? `; Path=${attrs.path}` : '; Path=/';
  const sameSite = attrs.sameSite ? `; SameSite=${attrs.sameSite}` : '; SameSite=Lax';
  const secure = attrs.secure ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}${expires}${path}${sameSite}${secure}`;
}

function getCookie(name) {
  return document.cookie
    .split('; ')
    .find(row => row.startsWith(`${name}=`))
    ?.split('=')[1];
}

function eraseCookie(name) {
  document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
}

// Utility: detect Do Not Track
function isDNTEnabled() {
  const dnt = navigator.doNotTrack || window.doNotTrack || navigator.msDoNotTrack;
  return dnt === '1' || dnt === 1;
}

// Hash utility placeholder for IP hash if provided externally
async function sha256Hex(input) {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Consent state structure
function defaultConsent() {
  const base = {
    consent_version: consentConfig.consentVersion,
    timestamp: new Date().toISOString(),
    region: null,
    ip_hash: null,
    categories: {
      necessary: true,
      functional: false,
      analytics: false,
      advertising: false
    }
  };

  // Respect DNT by defaulting to reject non-essential
  if (isDNTEnabled()) {
    base.categories.functional = false;
    base.categories.analytics = false;
    base.categories.advertising = false;
  }

  return base;
}

function readConsent() {
  try {
    const raw = getCookie(COOKIE_NAME);
    if (!raw) return null;
    const parsed = JSON.parse(decodeURIComponent(raw));
    return parsed;
  } catch (e) {
    console.warn('Failed to read consent cookie, resetting', e);
    return null;
  }
}

function writeConsent(consent) {
  setCookie(
    COOKIE_NAME,
    JSON.stringify(consent),
    consentConfig.cookieExpireDays,
    consentConfig.cookieAttributes
  );
}

function getConsent() {
  return readConsent() || null;
}

function setConsent(updater) {
  const current = readConsent() || defaultConsent();
  const next = typeof updater === 'function' ? updater(current) : { ...current, ...updater };
  next.timestamp = new Date().toISOString();
  next.consent_version = consentConfig.consentVersion;
  writeConsent(next);

  // Activate/deactivate scripts immediately
  applyScriptGating(next);

  // Dispatch global event
  const evt = new CustomEvent('consent:updated', { detail: next });
  window.dispatchEvent(evt);

  return next;
}

function onConsentChange(handler) {
  const listener = (e) => handler(e.detail);
  window.addEventListener('consent:updated', listener);
  return () => window.removeEventListener('consent:updated', listener);
}

// Script gating
function applyScriptGating(consent) {
  const allowed = consent.categories;
  // Find all lazy tags
  const candidates = document.querySelectorAll('script[type="text/plain"][data-category]');
  candidates.forEach((node) => {
    const category = node.getAttribute('data-category');
    if (!category || !allowed[category]) return;

    // Promote to active script once
    const alreadyActivated = node.getAttribute('data-activated') === 'true';
    if (alreadyActivated) return;

    const s = document.createElement('script');
    // Clone attributes excluding type and data attrs
    for (const { name, value } of Array.from(node.attributes)) {
      if (name === 'type') continue;
      if (name.startsWith('data-')) continue;
      s.setAttribute(name, value);
    }
    s.text = node.text || '';
    node.parentNode.insertBefore(s, node);
    node.setAttribute('data-activated', 'true');
  });

  // Best-effort disable in-memory trackers for newly disallowed categories
  window.__disableTrackers && window.__disableTrackers(Object.keys(allowed).filter(k => !allowed[k]));
}

// Public API
const ConsentManager = {
  getConsent,
  setConsent,
  onConsentChange,
  applyScriptGating,
  writeConsent,
  readConsent,
  eraseCookie,
  isDNTEnabled,
  sha256Hex,
  defaultConsent
};

export default ConsentManager;
